import React from 'react';
import { ControllerProps, useController, useFormContext } from 'react-hook-form';
import FormHelperText from '@material-ui/core/FormHelperText';
import { useDropzone } from 'react-dropzone';
import clsx from 'clsx';
import { Box, Button, Typography } from '@material-ui/core';

import { useStyles } from 'features/my-gift-cards/components/ImageFileField/styles';
import { useTranslation } from 'hooks/useTranslation';
import { FileUtils } from 'utils/FileUtils';
import { getImageSize } from 'features/app/utils/getImageSize';

import { ReactComponent as AddIcon } from 'assets/icons/add-circle-icon.svg';

interface ImageFileFieldProps extends Omit<ControllerProps, 'as' | 'render'> {
  containerClass?: string;
  titleLabelText: string;
  strictWidth?: number;
  strictHeight?: number;
  name: string;
}

export const ImageFileField: React.FC<ImageFileFieldProps> = ({
  containerClass = '',
  name,
  rules,
  titleLabelText,
  strictHeight,
  strictWidth,
}) => {
  const classes = useStyles();
  const { control, setError } = useFormContext();
  const { t } = useTranslation('my-gift-cards');

  const {
    field: { value: _value, onChange },
    fieldState: { error },
  } = useController({
    name,
    rules,
    control,
  });

  const wrappedOnChange = React.useCallback(
    (value: File | string | undefined) => {
      if (!value) return;

      if (value === 'remove') {
        onChange();
      } else {
        onChange(value);
      }
    },
    [onChange]
  );

  const handleDrop = React.useCallback(
    acceptedFiles => {
      wrappedOnChange(acceptedFiles[0]);
    },
    [wrappedOnChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    multiple: false,
    maxFiles: 1,
    accept: 'image/png, image/gif, image/jpeg',
  });

  const handleInputChange = (event: React.FormEvent<HTMLInputElement>) => {
    const file = (event.target as unknown as { files: File[] }).files[0];

    wrappedOnChange(file);
  };

  const value = _value as File | string | undefined;

  const imageSrc = React.useMemo(() => {
    if (typeof value === 'object') {
      const dataUrl = FileUtils.toObjectUrl(value as File);

      if (strictHeight || strictWidth) {
        getImageSize(dataUrl).then(({ height, width }) => {
          if (height !== strictHeight || width !== strictWidth) {
            setError(name, { message: t('ImageField.strictDimensionsError', { strictWidth, strictHeight }) });
          }
        });
      }

      return dataUrl;
    } else {
      return value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleImageRemove = () => {
    wrappedOnChange('remove');
  };

  return (
    <div className={`${containerClass} ${classes.root}`}>
      <div
        className={clsx(classes.imagePicker, containerClass, { [classes.active]: isDragActive })}
        {...getRootProps()}
      >
        <input
          type="file"
          className={classes.fileInput}
          {...getInputProps()}
          onChange={handleInputChange}
          multiple={false}
          accept="image/png, image/gif, image/jpeg"
          id={`image_file_input_${name}`}
        />
        {!value ? (
          <Box display="flex" flexDirection="row" alignItems="center" className={classes.addImage}>
            <AddIcon />

            <Box paddingLeft="20px">
              <Typography variant="h5" display={'inline'} className={classes.title}>
                {titleLabelText}
              </Typography>
              {strictHeight && strictWidth && (
                <Typography display={'inline'} variant="subtitle2" className={classes.subtitle}>
                  ({strictWidth}px x {strictHeight}px)
                </Typography>
              )}
              <Typography variant="h6" className={classes.subtitle}>
                {t('ImageField.subtitle')}
              </Typography>
            </Box>
          </Box>
        ) : (
          <img src={imageSrc} className={classes.image} />
        )}
      </div>
      {value && (
        <Box paddingTop={10}>
          {error && (
            <Box paddingBottom={4} display="flex" justifyContent="center">
              {error && <FormHelperText error>{error.message}</FormHelperText>}
            </Box>
          )}
          <Box display="flex" flexDirection="row" justifyContent="flex-end">
            <Button className={classes.bottomButton}>
              <Typography variant="h5" className={classes.removeButtonText} onClick={handleImageRemove}>
                {t('ImageField.removeImage')}
              </Typography>
            </Button>
            <label htmlFor={`image_file_input_${name}`} className={classes.changeImageLabel}>
              <Typography variant="h5" className={classes.changeImageText}>
                {t('ImageField.changeImage')}
              </Typography>
            </label>
          </Box>
        </Box>
      )}
    </div>
  );
};
